import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Divider,
  ListItemSecondaryAction,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { SavedLayout } from '../hooks/useLayoutStorage';
import { useLayoutStorage } from '../hooks/useLayoutStorage';
import { Dashboard } from '@shared/protos/v2/dashboard';

interface LoadLayoutDialogProps {
  open: boolean;
  onClose: () => void;
  onLoad: (savedLayout: SavedLayout) => void;
  currentDashboard: Dashboard;
}

export const LoadLayoutDialog = ({ open, onClose, onLoad }: LoadLayoutDialogProps) => {
  const [selectedLayout, setSelectedLayout] = useState<SavedLayout | null>(null);
  const { layouts, deleteLayout } = useLayoutStorage();

  useEffect(() => {
    if (!open) {
      setSelectedLayout(null);
    }
  }, [open]);

  const handleLoadLayout = () => {
    if (selectedLayout) {
      onLoad(selectedLayout);
    }
  };

  const handleDeleteLayout = (layoutId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    deleteLayout(layoutId);

    if (selectedLayout?.id === layoutId) {
      setSelectedLayout(null);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString() + ' ' + new Date(timestamp).toLocaleTimeString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Load Saved Layout</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a saved layout to restore. You can load layouts from any dashboard.
          </Typography>

          {layouts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No saved layouts found.
            </Typography>
          ) : (
            <List>
              {layouts.map((layout, index) => (
                <React.Fragment key={layout.id}>
                  <ListItemButton
                    selected={selectedLayout?.id === layout.id}
                    onClick={() => setSelectedLayout(layout)}
                    sx={{ borderRadius: 1, mb: 1 }}
                  >
                    <ListItemText
                      primary={layout.name}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            From: {layout.dashboard.name}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Saved: {formatDate(layout.timestamp)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Widgets: {layout.dashboard.json?.widgets?.length || 0}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={e => handleDeleteLayout(layout.id, e)} size="small">
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                  {index < layouts.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleLoadLayout} variant="contained" disabled={!selectedLayout}>
          Load Layout
        </Button>
      </DialogActions>
    </Dialog>
  );
};
